import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, X, AlertTriangle } from "lucide-react";

/** Dispatcher tool: scan the database for TEST bookings (fake names, 555
 *  phone numbers, example.com emails) and purge them so they never show on
 *  the live dashboard. Suspected records are pre-selected; the dispatcher
 *  reviews before deleting. */
export default function TestDataCleanup({ onDone }) {
  const [open, setOpen] = useState(false);
  const [scan, setScan] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const doScan = async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/test-data/scan");
      setScan(r.data);
      setSelected(new Set(r.data.customers.filter(c => c.suspected).map(c => c.key)));
      setOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed");
    } finally { setBusy(false); }
  };

  const toggle = (key) => setSelected(s => {
    const n = new Set(s);
    n.has(key) ? n.delete(key) : n.add(key);
    return n;
  });

  const purge = async () => {
    if (selected.size === 0) return toast.error("Nothing selected");
    const trips = scan.customers.filter(c => selected.has(c.key)).reduce((a, c) => a + c.trips, 0);
    if (!window.confirm(`Permanently delete ${trips} trip(s) from ${selected.size} customer record(s)?\n\nThis removes their bookings, reminders and invoices. This cannot be undone.`)) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/test-data/purge", { keys: [...selected] });
      toast.success(`Removed ${r.data.bookings} bookings, ${r.data.invoices} invoices, ${r.data.reminders} reminders`);
      setOpen(false);
      setScan(null);
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purge failed");
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button data-testid="cleanup-scan-btn" onClick={doScan} disabled={busy}
        className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] hover:text-red-400 border border-white/10 hover:border-red-400/40 px-2.5 py-1.5 transition-colors disabled:opacity-50">
        🧹 {busy ? "Scanning…" : "Remove test data"}
      </button>
    );
  }

  const suspected = scan.customers.filter(c => c.suspected);
  const others = scan.customers.filter(c => !c.suspected);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 overflow-y-auto p-4" data-testid="cleanup-modal" onClick={() => setOpen(false)}>
      <div className="bg-[#0A0A0A] border border-white/15 max-w-2xl mx-auto my-8 p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-serif text-xl text-[#F5F2EB]">Remove test data</h3>
          <button data-testid="cleanup-close" onClick={() => setOpen(false)} className="text-[#71717A] hover:text-white"><X size={18}/></button>
        </div>
        <p className="text-[11px] text-[#71717A] mb-4">
          {scan.suspected_count} suspected test customer(s) found ({scan.suspected_trips} trips) — pre-selected below.
          Review, adjust, then delete. Real customers are listed underneath (unselected).
        </p>

        <div className="space-y-1.5 max-h-[45vh] overflow-y-auto pr-1">
          {suspected.length === 0 && <div className="text-xs text-[#71717A]">No suspected test data found 🎉</div>}
          {[...suspected, ...others].map(c => (
            <label key={c.key} data-testid={`cleanup-row-${c.key}`}
              className={`flex items-center gap-3 border p-2.5 cursor-pointer transition-colors ${selected.has(c.key) ? "border-red-400/50 bg-red-500/5" : "border-white/5 hover:border-white/15"}`}>
              <input type="checkbox" checked={selected.has(c.key)} onChange={() => toggle(c.key)} className="accent-red-500 shrink-0"/>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-[#F5F2EB] truncate">
                  {c.name || c.email}
                  {c.suspected && <span className="ml-2 text-[9px] uppercase tracking-[0.15em] text-red-400 border border-red-400/40 px-1.5 py-0.5">test?</span>}
                </div>
                <div className="text-[10px] text-[#71717A] truncate">{c.email || "no email"} · {c.phone || "no phone"}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-xs text-[#D4AF37]">{c.trips} trip{c.trips === 1 ? "" : "s"} · ${c.total.toFixed(0)}</div>
                {c.paid_trips > 0 && <div className="text-[9px] uppercase tracking-[0.15em] text-emerald-400">{c.paid_trips} paid</div>}
              </div>
            </label>
          ))}
        </div>

        <div className="flex items-center gap-2 mt-4 pt-3 border-t border-white/10">
          <AlertTriangle size={14} className="text-amber-400 shrink-0"/>
          <div className="text-[10px] text-[#A1A1AA] flex-1">Deletes bookings, reminders & invoices for the selected records. Cannot be undone.</div>
          <button data-testid="cleanup-purge-btn" onClick={purge} disabled={busy || selected.size === 0}
            className="bg-red-600 hover:bg-red-500 text-white px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] disabled:opacity-40 flex items-center gap-1.5">
            <Trash2 size={12}/> Delete {selected.size} selected
          </button>
        </div>
      </div>
    </div>
  );
}
